import { FC, ReactNode, useMemo } from 'react'
import { Flex } from '../flex/Flex'
import { composeClasses } from '@/utils/compose-classes'
import type { GapType } from '@/interfaces/types'

export type BadgeType = 'blue' | 'red' | 'green' | 'yellow' | 'gray'

export interface BadgeProps {
  /**
   * The value of the badge
   */
  value: ReactNode
  /**
   * The color of the badge
   * @default gray
   */
  type?: BadgeType
  /**
   * The variant of the badge (light or dark)
   * @default light
   */
  variant?: 'light' | 'dark'
  /**
   * The size of the badge
   * @default medium
   */
  size?: 'small' | 'medium'
  /**
   * The gap between the dot and the value
   * @default gap-1
   */
  gap?: GapType
  /**
   * Custom colors for the badge
   */
  customColors?: {
    bgColor: string
    textColor: string
  }
  /**
   * The className of the badge
   * */
  className?: string
  /**
   * The left icon of the badge
   */
  leftIcon?: ReactNode
  /**
   * The right icon of the badge
   */
  rightIcon?: ReactNode
}

const assignedColors = {
  gray: {
    light: {
      bgColor: 'bg-surface-gray-10',
      textColor: 'text-primary'
    },
    dark: {
      bgColor: 'bg-notif-gray',
      textColor: 'text-white'
    }
  },
  blue: {
    light: {
      bgColor: 'bg-notif-blue-01',
      textColor: 'text-blue'
    },
    dark: {
      bgColor: 'bg-notif-blue',
      textColor: 'text-white'
    }
  },
  red: {
    light: {
      bgColor: 'bg-notif-red-01',
      textColor: 'text-notif-red'
    },
    dark: {
      bgColor: 'bg-notif-red',
      textColor: 'text-white'
    }
  },
  green: {
    light: {
      bgColor: 'bg-notif-green-01',
      textColor: 'text-notif-green'
    },
    dark: {
      bgColor: 'bg-notif-green',
      textColor: 'text-white'
    }
  },
  yellow: {
    light: {
      bgColor: 'bg-notif-yellow-01',
      textColor: 'text-notif-yellow'
    },
    dark: {
      bgColor: 'bg-notif-yellow',
      textColor: 'text-white'
    }
  }
} as const

const assignedSizes = {
  small: {
    fontSize: 'text-body-xs !font-[645]',
    padding: 'px-2',
    sizeIcon: 'text-[16px]'
  },
  medium: {
    fontSize: 'text-body-s !font-[759]',
    padding: 'px-4',
    sizeIcon: 'text-[24px]'
  }
} as const

export const Badge: FC<BadgeProps> = ({
  value,
  type = 'gray',
  gap = 'gap-1',
  className = '',
  customColors,
  size = 'medium',
  variant = 'light',
  rightIcon,
  leftIcon
}) => {
  const { fontSize, padding } = assignedSizes[size]

  const colorsByType = useMemo(
    () => assignedColors[type][variant],
    [type, variant]
  )

  const badgeColors = useMemo(
    () => customColors ?? colorsByType,
    [customColors, colorsByType]
  )

  return (
    <Flex
      items="center"
      justify="center"
      gap={gap}
      role="badge"
      className={composeClasses(
        badgeColors.bgColor,
        className,
        padding,
        'w-fit rounded-3xl leading-normal uppercase py-1'
      )}
    >
      {leftIcon}
      <span
        data-testid="value-badge"
        className={composeClasses(
          'flex items-center justify-center',
          fontSize,
          badgeColors.textColor
        )}
      >
        {value}
      </span>
      {rightIcon}
    </Flex>
  )
}

export default Badge
